import mapValues from 'lodash/fp/mapValues';
import { generateMedia } from 'styled-media-query';

interface Color {
  [key: string]: string;
}

export const colorPalette: Color = {
  neutral80: '#C4C4C4',
  neutralVariant80: '#C3C6CF',
  neutral150: '#131414',
  neutral200: '#242526',
  neutral250: '#5B5F66',
  neutral350: '#5C5E61',
  neutral400: '#85898C',
  neutral450: '#A2A7AB',
  neutral500: '#BBBFC4',
  neutral550: '#CBD1D6',
  neutral650: '#D8DDE3',
  neutral850: '#EBEFF2',
  neutralVariant90: '#DFE4E8',
  neutral950: '#F7FAFC',
  white: '#FFF',
  black: '#000',
  navy300: '#0D253D',
  navy500: '#1A4A7A',
  navy650: '#2A77C5',
  navy740: '#448ED7',
  navy700: 'rgba(26, 74, 122, 1)',
  navy750: 'rgba(26, 74, 122, 0.50)',
  navy850: 'rgba(26, 74, 122, 0.30)',
  navy900: 'rgba(26, 74, 122, 0.12)',
  navy950: 'rgba(26, 74, 122, 0.08)',
  navy975: '#DAE8F7',
  navy980: '#DAE8F7',
  navy990: '#EEF7FF',
  orange500: '#FF8500',
  orange350: '#9F5200',
  orange650: '#FFA340',
  blue250: '#085161',
  blue350: '#0E86A1',
  blue450: '#14BCE2',
  blue550: '#46D0EF',
  blue975: '#E5F8FD',
  green200: '#07432E',
  green300: '#0F855C',
  green400: '#16C88A',
  green975: '#E7FCF5',
  yellow200: '#544100',
  yellow450: '#FFCD28',
  yellow500: '#FFD652',
  yellow300: '#A88100',
  yellow750: '#FFE693',
  yellow950: '#FFF5D4',
  red350: '#CF1C00',
  red200: '#530B00',
  red250: '#7C1100',
  red450: '#FF4023',
  red500: '#FF644D',
  red550: '#FF7863',
  red650: '#FF8B79',
  red900: '#FFC5BC',
  red950: '#FFD8D2',
  red975: '#FFECE9',
  magenta950: '#E86BEB',
  purple200: '#221f4b',
  purple450: '#665CD6',
  purple550: '#8987F6',
  purple900: '#DCDEFF',
  purple975: '#f5f4ff',
  plum250: '#992881',
  plum200: '#6D1D5A',
  plum500: '#FE6BF5',
  plum550: '#FF7FFE',
  plum650: '#FF95FF',
  plum950: '#FCE6FF',
  plum975: '#FCEEFF',
  plum995: '#FDF7FF',
};

export const colorTokens = {
  text: {
    primary: colorPalette.neutral200,
    secondary: colorPalette.neutral350,
    disabled: colorPalette.neutral500,
    contrast: colorPalette.white,
  },
  border: {
    main: colorPalette.neutral550,
    light: colorPalette.neutral850,
    dark: colorPalette.neutral450,
  },
  primary: {
    main: colorPalette.navy500,
    dark: colorPalette.navy300,
    light: colorPalette.navy650,
    contrast: colorPalette.white,
    hover: colorPalette.navy975,
    selected: colorPalette.navy950,
    focus: colorPalette.navy900,
    focusVisible: colorPalette.navy850,
    outlineBorder: colorPalette.navy750,
    disabled: colorPalette.neutral450,
    borderDark: colorPalette.navy80,
  },
  secondary: {
    main: colorPalette.orange500,
    dark: colorPalette.orange350,
    light: colorPalette.orange650,
    contrast: colorPalette.white,
    disabled: colorPalette.neutral450,
  },
  info: {
    main: colorPalette.blue350,
    dark: colorPalette.blue250,
    light: colorPalette.blue450,
    contrast: colorPalette.white,
    outlineBorder: colorPalette.blue550,
  },
  success: {
    main: colorPalette.green300,
    dark: colorPalette.green200,
    light: colorPalette.green400,
    contrast: colorPalette.white,
    buttonHover: colorPalette.green975,
    outlineBorder: 'rgba(15, 133, 92, 0.50)',
  },
  warning: {
    main: colorPalette.yellow500,
    dark: colorPalette.yellow300,
    light: colorPalette.yellow750,
    contrast: colorPalette.neutral150,
  },
  error: {
    main: colorPalette.red350,
    dark: colorPalette.red250,
    light: colorPalette.red450,
    buttonActive: colorPalette.red900,
    button: colorPalette.red950,
    buttonHover: colorPalette.red975,
    contrast: colorPalette.white,
    outlineBorder: colorPalette.red550,
  },
  background: {
    default: colorPalette.white,
    black: colorPalette.black,
    disabled: colorPalette.neutral850,
  },
  chip: {
    default: colorPalette.navy975,
    light: colorPalette.navy980,
    dark: colorPalette.navy700,
    border: colorPalette.navy750,
  },
  tonal: {
    dark: colorPalette.navy975,
    light: colorPalette.navy990,
    contrast: colorPalette.white,
  },
  iconButton: {
    default: colorPalette.neutral400,
  },
  // TODO #22334: Update incident keys to match priorities from API
  incident: {
    severe: colorPalette.red500,
    high: colorPalette.red975,
    highBorder: colorPalette.red350,
    mid: colorPalette.yellow950,
    midBorder: colorPalette.yellow300,
    low: colorPalette.white,
    lowBorder: colorPalette.neutral550,
  },
  note: {
    ride: colorPalette.yellow950,
  },
  avatar: [
    colorPalette.purple450,
    colorPalette.yellow450,
    colorPalette.orange500,
    colorPalette.magenta950,
    colorPalette.green300,
    colorPalette.red500,
    colorPalette.blue450,
    colorPalette.navy550,
  ],
  surface: {
    main: colorPalette.navy990,
    light: colorPalette.neutral950,
    dark: colorPalette.neutral850,
  },
  plum: {
    main: colorPalette.plum500,
    dark: colorPalette.plum250,
    light: colorPalette.plum650,
    hovered: '#FF7FFE04',
    selected: '#FF7FFE08',
    focus: '#FF7FFE12',
    focusVisible: '#FF7FFE30',
    outlineBorder: '#FF7FFE50',
  },
};

export const font = {
  size: {
    base: 13,
    medium: 16,
    large: 24,
  },
  weight: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
};

const breakpoints = {
  desktop: 1440,
  tablet: 900,
  mobile: 0,
};

export const mediaQuery = generateMedia(mapValues((v) => `${v}px`, breakpoints));

const headingHeights = {
  withTitle: 151,
  withTitleTabs: 201,
  withTitleTabsFilters: 291,
};

const tableHeadHeight = 76;

const mobileSidebarHeight = 68;

export const variables = {
  font,
  colorPalette,
  colorTokens,
  breakpoints,
  mediaQuery,
  headingHeights,
  tableHeadHeight,
  mobileSidebarHeight,
};
